import React, { createContext, useContext, useState, ReactNode } from 'react';

interface UnlockedMedia {
    mediaId: string;
    [key: string]: any;
}

interface RestrictionState {
    unlockedMedias: UnlockedMedia[];
}

interface RestrictionContextType extends RestrictionState {
    setUnlockedMedia: (_media: UnlockedMedia) => void;
    setUnlockedMedias: (_medias: UnlockedMedia[]) => void;
}

const initialState: RestrictionState = {
    unlockedMedias: []
};

const getUniqueUnlockMedias = (currentMedias: UnlockedMedia[], newMedia: UnlockedMedia): UnlockedMedia[] => {
    let newData = currentMedias ?? [];
    if (!newData.find((n) => n.mediaId === newMedia.mediaId)) {
        return [...newData, newMedia];
    }

    newData = newData.map((s) => (newMedia.mediaId === s.mediaId ? newMedia : s));
    return newData;
};

const RestrictionContext = createContext<RestrictionContextType | undefined>(undefined);

export const RestrictionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [state, setState] = useState<RestrictionState>(initialState);

    const setUnlockedMedia = (unlockedMedia: UnlockedMedia) =>
        setState((s) => ({
            ...s,
            unlockedMedias: getUniqueUnlockMedias(s.unlockedMedias, unlockedMedia),
        }));

    const setUnlockedMedias = (unlockedMedias: UnlockedMedia[]) => setState((s) => ({ ...s, unlockedMedias }));

    const value: RestrictionContextType = {
        ...state,
        setUnlockedMedia,
        setUnlockedMedias,
    };

    return <RestrictionContext.Provider value={value}>{children}</RestrictionContext.Provider>;
};

export const useRestriction = (): RestrictionContextType => {
    const context = useContext(RestrictionContext);
    if (!context) {
        throw new Error('useRestriction must be used within a RestrictionProvider');
    }
    return context;
};
